import { Base64File } from '@synergy/models';
import { Box, FormHelperText, Stack } from '@mui/material';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { ImageRatio } from '@synergy/models';
import { MAX_FILE_SIZE } from '@synergy/config';
import { PaloFormSection } from '@synergy/components-palo';
import { PoolTypography } from '@synergy/components';
import { base64encode, isMimeTypeImage } from '@synergy/utils';
import { useState } from 'react';

import { OptionalSectionTitle } from '../../../../components/optional-section-title';
import { PictureDropzone } from '../../../../components/picture-dropzone';
import { SupplierSpecialOffersOverviewFormValues } from '../models/supplier-special-offers-overview-form-values';

export const SupplierSpecialOffersDetailOverviewSupplierImage = () => {
    const [newImageFile, setNewImageFile] = useState<Base64File | undefined>(undefined);

    const handleDrop =
        (form: FormikProps<SupplierSpecialOffersOverviewFormValues>, name: string) =>
        async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
                    form.setFieldError(name, 'app.error.jpgGifWebPFileType');
                } else if (rejectedFiles[0].errors[0].code === ErrorCode.FileTooLarge) {
                    form.setFieldError(name, 'app.error.maxFiveMb');
                } else {
                    form.setFieldError(name, 'app.error.maxOneFile');
                }

                form.setFieldTouched(name, true, false);

                return;
            }

            if (!isMimeTypeImage(acceptedFiles[0]?.type)) {
                form.setFieldError(name, 'app.error.jpgGifWebPFileType');
                form.setFieldTouched(name, true, false);
                return;
            }

            const image = { name: acceptedFiles[0].name, contentBase64: await base64encode(acceptedFiles[0]) };

            if (name === 'image') {
                setNewImageFile(image);
            }

            form.setFieldValue(name, image);
        };

    return (
        <PaloFormSection
            title={
                <OptionalSectionTitle>
                    <FormattedMessage id="app.contentManagement.supplierSpecialOffers.picture" />
                </OptionalSectionTitle>
            }
        >
            <Stack gap={2}>
                <FormattedMessage id="app.contentManagement.supplierSpecialOffers.pictureText" />

                <Field name="image">
                    {({ field, form, meta }: FieldProps<Base64File>) => (
                        <Box sx={{ width: 255 }}>
                            <PictureDropzone
                                onDrop={handleDrop(form, field.name)}
                                error={!!meta.error && !!meta.touched}
                                maxSize={MAX_FILE_SIZE}
                                path={newImageFile?.contentBase64 ?? field.value?.pathname}
                                imageRatio={ImageRatio.SixteenByNine}
                                alt={field.value?.name}
                            >
                                <PoolTypography
                                    typography="body2"
                                    component="span"
                                    color="grey.700"
                                    id="app.contentManagement.supplierSpecialOffers.pictureDesktop"
                                />
                                <PoolTypography
                                    typography="body2"
                                    component="span"
                                    color="grey.700"
                                    id="app.contentManagement.supplierSpecialOffers.pictureDesktopSize"
                                />
                            </PictureDropzone>
                            {!!meta.error && !!meta.touched && (
                                <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </Box>
                    )}
                </Field>
            </Stack>
        </PaloFormSection>
    );
};
